import { api } from '../lib/api';
import type {
  Candidate,
  CandidateStats,
  JobApplication,
  Experience,
  Education,
  ApiResponse,
  PaginatedResponse,
  Testimonial,
} from '../types';

class CandidateService {
  async getProfile(): Promise<Candidate> {
    const response = await api.get<unknown>('/candidate/profile');
    // Handle different response formats: { data: Candidate } or Candidate directly
    const anyResp = response as Record<string, unknown>;
    if (anyResp && typeof anyResp === 'object' && 'data' in anyResp && anyResp.data) {
      return anyResp.data as Candidate;
    }
    return response as Candidate;
  }

  // Depoimento do candidato (página de dashboard)
  async getMyTestimonial(): Promise<Testimonial | null> {
    const resp = await api.get<unknown>('/candidate/testimonial').catch(() => null);
    if (!resp) return null;
    const anyResp = resp as any;
    if (anyResp && typeof anyResp === 'object' && 'data' in anyResp) return anyResp.data as Testimonial;
    return resp as Testimonial;
  }

  async submitTestimonial(content: string): Promise<Testimonial> {
    // Backend espera { message: string }
    const response = await api.post<ApiResponse<Testimonial>>('/candidate/testimonial', { message: content });
    return response.data;
  }

  async updateProfile(data: Partial<Candidate>): Promise<Candidate> {
    const response = await api.put<unknown>('/candidate/profile', data);
    const anyResp = response as Record<string, unknown>;
    if (anyResp && typeof anyResp === 'object' && 'data' in anyResp && anyResp.data) {
      return anyResp.data as Candidate;
    }
    return response as Candidate;
  }

  async uploadPhoto(file: File): Promise<Candidate> {
    const response = await api.uploadFile<ApiResponse<Candidate>>('/candidate/photo', file);
    return response.data;
  }

  async uploadResume(file: File): Promise<Candidate> {
    const response = await api.uploadFile<ApiResponse<Candidate>>('/candidate/resume', file);
    return response.data;
  }

  async getStats(): Promise<CandidateStats> {
    const response = await api.get<ApiResponse<CandidateStats>>('/candidate/stats');
    return response.data;
  }

  async getMyApplications(params?: {
    page?: number;
    per_page?: number;
    status?: string;
  }): Promise<PaginatedResponse<JobApplication>> {
    const response = await api.get<PaginatedResponse<JobApplication>>('/candidate/applications', {
      params: params as Record<string, string | number | boolean>,
    });
    return response;
  }

  async getApplicationById(id: number): Promise<JobApplication> {
    const response = await api.get<ApiResponse<JobApplication>>(`/candidate/applications/${id}`);
    return response.data;
  }

  async cancelApplication(id: number): Promise<void> {
    await api.delete(`/candidate/applications/${id}`);
  }

  async addExperience(data: Experience): Promise<Experience> {
    const response = await api.post<unknown>('/candidate/experiences', data);
    const anyResp = response as Record<string, unknown>;
    if (anyResp && typeof anyResp === 'object' && 'data' in anyResp && anyResp.data) {
      return anyResp.data as Experience;
    }
    return response as Experience;
  }

  async updateExperience(id: number, data: Experience): Promise<Experience> {
    const response = await api.put<unknown>(`/candidate/experiences/${id}`, data);
    const anyResp = response as Record<string, unknown>;
    if (anyResp && typeof anyResp === 'object' && 'data' in anyResp && anyResp.data) {
      return anyResp.data as Experience;
    }
    return response as Experience;
  }

  async deleteExperience(id: number): Promise<void> {
    await api.delete(`/candidate/experiences/${id}`);
  }

  async addEducation(data: Education): Promise<Education> {
    const response = await api.post<unknown>('/candidate/educations', data);
    const anyResp = response as Record<string, unknown>;
    if (anyResp && typeof anyResp === 'object' && 'data' in anyResp && anyResp.data) {
      return anyResp.data as Education;
    }
    return response as Education;
  }

  async updateEducation(id: number, data: Education): Promise<Education> {
    const response = await api.put<unknown>(`/candidate/educations/${id}`, data);
    const anyResp = response as Record<string, unknown>;
    if (anyResp && typeof anyResp === 'object' && 'data' in anyResp && anyResp.data) {
      return anyResp.data as Education;
    }
    return response as Education;
  }

  async deleteEducation(id: number): Promise<void> {
    await api.delete(`/candidate/educations/${id}`);
  }

  async addSkill(skill: string): Promise<Candidate> {
    const response = await api.post<ApiResponse<Candidate>>('/candidate/skills', { skill });
    return response.data;
  }

  async removeSkill(skill: string): Promise<Candidate> {
    const response = await api.delete<ApiResponse<Candidate>>(`/candidate/skills/${skill}`);
    return response.data;
  }

  async getProfileCompleteness(): Promise<{
    can_apply: boolean;
    missing_fields: string[];
    completeness_percentage: number;
    requirements: {
      has_resume: boolean;
      has_bio: boolean;
      has_phone: boolean;
      has_experience: boolean;
      has_education: boolean;
    };
  }> {
    const response = await api.get<any>('/candidate/profile-completeness');
    // Handle wrapped response
    if (response && typeof response === 'object' && 'data' in response && response.data) {
      return response.data;
    }
    return response;
  }

  // Excluir conta do candidato (LGPD)
  async deleteAccount(): Promise<void> {
    // Tenta endpoints comuns; o backend pode usar nomes diferentes
    const endpoints = [
      '/candidate/account',
      '/candidate/delete-account',
      '/auth/delete-account',
      '/user/delete-account',
      '/candidate/profile', // alguns backends aceitam DELETE no mesmo recurso
    ];
    let lastError: unknown = null;
    for (const url of endpoints) {
      try {
        await api.delete(url as any);
        return;
      } catch (e) {
        lastError = e;
      }
    }
    throw lastError instanceof Error ? lastError : new Error('Não foi possível excluir a conta');
  }

  async getCandidateById(id: number, jobId?: number): Promise<Candidate> {
    const params: Record<string, string | number | boolean> = {};
    if (jobId) params.job_id = jobId;
    const response = await api.get<unknown>(`/candidates/${id}`, { params });
    // Handle different response formats: { data: Candidate } or Candidate directly
    const anyResp = response as Record<string, unknown>;
    if (anyResp && typeof anyResp === 'object' && 'data' in anyResp && anyResp.data) {
      return anyResp.data as Candidate;
    }
    return response as Candidate;
  }

  async searchCandidates(params?: {
    search?: string;
    skills?: string[];
    skills_mode?: 'any' | 'all';
    city?: string;
    area_id?: number;
    job_id?: number;
    page?: number;
    per_page?: number;
  }): Promise<PaginatedResponse<Candidate>> {
    if (!params) return api.get<PaginatedResponse<Candidate>>('/candidates/search');
    const sp = new URLSearchParams();
    if (params.search) sp.append('search', params.search);
    if (params.city) sp.append('city', params.city);
    if (typeof params.area_id === 'number') sp.append('area_id', String(params.area_id));
    if (typeof params.page === 'number') sp.append('page', String(params.page));
    if (typeof params.per_page === 'number') sp.append('per_page', String(params.per_page));
    if (params.skills && Array.isArray(params.skills) && params.skills.length > 0) {
      // Array com colchetes: skills[]=React&skills[]=SQL
      params.skills.forEach((s) => sp.append('skills[]', s));
    }
    if (params.skills_mode) sp.append('skills_mode', params.skills_mode);
    if (typeof params.job_id === 'number') sp.append('job_id', String(params.job_id));

    const qs = sp.toString();
    const endpoint = qs ? `/candidates/search?${qs}` : '/candidates/search';
    return api.get<PaginatedResponse<Candidate>>(endpoint);
  }
}

export const candidateService = new CandidateService();
